import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { User } from '@generated/models';
import { AdelaideService } from '@web/core/adelaide/adelaide.service';
import { NavBackMenuLabelComponent } from '@web/core/nav-back-stack/nav-back-menu-label.component';
import { navBackMenuAccent } from '@web/core/nav-back-stack/nav-back-menu-visual';
import { NavBackStackService } from '@web/core/nav-back-stack/nav-back-stack.service';
import { NavBackStackEntry } from '@web/core/nav-back-stack/nav-back-stack.types';
import { Tenant } from '@web/core/services/tenants/tenant.abstract';
import { environment } from '@web/env/environment';
import { SearchModalComponent, SearchModalOptions } from '@web/shared/components/search/search-modal.component';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogService } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-main-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AvatarModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ButtonModule,
    RouterLink,
    MenuModule,
    ContextMenuModule,
    TooltipModule,
    NavBackMenuLabelComponent,
  ],
  providers: [DialogService],
  template: ` @let _user = user();

    <div class="flex w-full justify-between items-center">
      <div [routerLink]="'./dashboard'" class="cursor-pointer">
        <img [src]="tenant().logo" class="h-11 object-contain" alt="logo" />
      </div>
      <div class="flex w-90 items-center gap-2">
        <div #backButtonTarget class="shrink-0" (contextmenu)="onBackContextMenu($event)">
          <p-button
            icon="pi pi-arrow-left"
            size="small"
            variant="text"
            [rounded]="true"
            [disabled]="!navBackStack.canGoBack()"
            [attr.aria-label]="backAriaLabel"
            [pTooltip]="backTooltip"
            i18n-pTooltip
            tooltipPosition="bottom"
            (click)="onBackClick($event)"
          />
        </div>
        @if (navBackStack.hasMenuEntries()) {
          <p-contextmenu
            [target]="backButtonTarget"
            appendTo="body"
            styleClass="nav-back-contextmenu"
            [model]="backMenuItems()"
          >
            <ng-template pTemplate="item" let-item>
              <app-nav-back-menu-label class="block cursor-pointer px-2.5 py-1.5" [entry]="asNavBackEntry(item.data)" />
            </ng-template>
          </p-contextmenu>
        }
        <p-iconfield class="min-w-0 flex-1">
          <p-inputicon styleClass="pi pi-search" />
          <input type="text" pInputText placeholder="Search" i18n-placeholder class="w-full" (click)="openSearch()" />
        </p-iconfield>
      </div>
      <div class="min-w-40 flex justify-end">
        <p-menu #menu [model]="userMenuItems" [popup]="true" appendTo="body" />
        <p-button size="small" variant="text" [rounded]="true" (click)="menu.toggle($event)">
          <span> {{ _user?.displayName }} </span>
          <p-avatar icon="pi pi-user" class="mr-2" shape="circle" />
        </p-button>
        @if (adelaideEnabled) {
          <p-button
            outlined="true"
            [style]="{ '--p-button-padding-y': 0, '--p-button-padding-x': 0 }"
            (click)="toggleAdelaide()"
          >
            <div
              class="size-10 bg-cover"
              [style]="{
                backgroundImage: 'url(/img/adelaide.jpg)',
              }"
            ></div>
          </p-button>
        }
      </div>
    </div>`,
  styles: `
    ::ng-deep .p-contextmenu.nav-back-contextmenu {
      min-width: 18rem;
    }

    ::ng-deep .nav-back-contextmenu .p-contextmenu-root-list {
      padding: 0.25rem;
    }

    ::ng-deep .nav-back-contextmenu .p-contextmenu-item-content {
      cursor: pointer;
      border-radius: 0.375rem;
      box-shadow: inset 3px 0 0 var(--nav-back-accent, transparent);
    }

    ::ng-deep .nav-back-contextmenu .p-contextmenu-item:not(.p-disabled) > .p-contextmenu-item-content:hover {
      background: color-mix(in srgb, var(--nav-back-accent, var(--p-content-hover-background)) 14%, white);
    }
  `,
})
export class MainHeaderComponent {
  readonly user = input.required<User | null>();
  readonly tenant = input.required<Tenant>();

  readonly navBackStack = inject(NavBackStackService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly adelaideService = inject(AdelaideService);

  readonly adelaideEnabled = environment.adelaide;

  readonly backTooltip = $localize`Go back`;
  readonly backAriaLabel = $localize`Go back`;

  readonly backMenuItems = computed((): MenuItem[] =>
    this.navBackStack.menuEntries().map((entry, index) => ({
      label: entry.label ?? '',
      data: entry,
      style: { '--nav-back-accent': navBackMenuAccent(entry) },
      command: () => {
        void this.onBackMenuSelect(index);
      },
    }))
  );

  asNavBackEntry(data: unknown): NavBackStackEntry {
    return data as NavBackStackEntry;
  }

  private readonly dialog = inject(DialogService);

  openSearch() {
    this.dialog.open(SearchModalComponent, SearchModalOptions);
  }

  toggleAdelaide() {
    if (this.adelaideEnabled) {
      this.adelaideService.toggle();
    }
  }

  async onBackClick(event: MouseEvent): Promise<void> {
    event.preventDefault();
    if (!this.navBackStack.canGoBack()) {
      return;
    }

    const proceed = await this.navBackStack.dirtyGate.confirmProceed(this.confirmationService);
    if (!proceed) {
      return;
    }

    if (this.navBackStack.isCurrentUrlOut()) {
      this.navBackStack.navigateToStackTopWithoutPop();
      return;
    }

    this.navBackStack.pop();
  }

  onBackContextMenu(event: MouseEvent): void {
    if (!this.navBackStack.hasMenuEntries()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  async onBackMenuSelect(index: number): Promise<void> {
    const proceed = await this.navBackStack.dirtyGate.confirmProceed(this.confirmationService);
    if (!proceed) {
      return;
    }

    this.navBackStack.jump(index);
  }

  readonly userMenuItems: MenuItem[] = [
    {
      label: $localize`My Profile`,
      routerLink: ['/', 'profile'],
      icon: 'pi pi-user',
      styleClass: 'leading-none',
    },
    {
      label: $localize`Logout`,
      routerLink: ['/', 'auth', 'logout'],
      icon: 'pi pi-sign-out',
      styleClass: 'leading-none',
    },
  ];
}
